var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useContext, useEffect, useState } from 'react';
import UserContext from '../UserContext';
import MessageContext, { error, success } from '../MessageContext';
import { SectionTitle } from '../components/StyledComponent';
import { createEmailTemplate, deleteEmailTemplate, getEmailsLog, getEmailTemplate, updateEmailTemplate } from '../rest/BackendApi';
import TabContext from '@mui/lab/TabContext';
import { Alert, Box, Button, Grid, Tab, TextField } from '@mui/material';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import Loading from '../components/Loading';
import MUIDataTable from 'mui-datatables';
import { displayTime, displayYearMonth } from '../util/TimeUtil';
import { getDatatableConfig } from '../util/LocalStorageUtil';
import Editor from '../components/Editor/Editor';
import { EMAIL_TAGS } from '../model/EmailTags';
import { isBlank, isBlankHtml } from '../util/StringUtil';
import { getFullName } from '../util/DataUtil';
export default function EmailsPage() {
    const userContext = useContext(UserContext);
    const messageContext = useContext(MessageContext);
    const user = userContext === null || userContext === void 0 ? void 0 : userContext.user;
    const [emailLogs, setEmailLogs] = useState([]);
    const [tab, setTab] = useState('1');
    const [loading, setLoading] = useState(true);
    const [editMode, setEditMode] = useState(false);
    const [emailTemplate, setEmailTemplate] = useState(undefined);
    const fetchEmailLogs = () => {
        setLoading(true);
        getEmailsLog().then(data => {
            setEmailLogs(data);
            setLoading(false);
        });
    };
    const updateEmailTemplateState = (newState) => {
        if (!emailTemplate) {
            setEmailTemplate(newState);
        }
        else {
            setEmailTemplate(Object.assign(Object.assign({}, emailTemplate), newState));
        }
    };
    const fetchEmailTemplate = () => {
        setLoading(true);
        getEmailTemplate().then(data => {
            setEmailTemplate(data);
            setLoading(false);
        });
    };
    useEffect(() => {
        if (tab == '1' && emailLogs.length == 0) {
            fetchEmailLogs();
        }
        else if (tab == '2' && !emailTemplate) {
            fetchEmailTemplate();
        }
    }, [tab]);
    const handleChange = (event, newValue) => {
        setTab(newValue);
    };
    function createColumn(name, label, customFormat, extraOptions) {
        return {
            name: name,
            label: label,
            options: Object.assign({ filter: false, sort: false, customBodyRenderLite: (dataIndex) => {
                    var _a, _b;
                    const emailLog = emailLogs[dataIndex];
                    return _jsx("div", { children: customFormat ? customFormat((_a = emailLog[name]) === null || _a === void 0 ? void 0 : _a.toString(), emailLog) : (_b = emailLog[name]) === null || _b === void 0 ? void 0 : _b.toString() });
                } }, extraOptions),
        };
    }
    const columns = [
        createColumn('dateSent', 'Date sent', displayTime, { sortDirection: 'desc' }),
        createColumn('recipientOrganisationName', 'Recipient Organisation'),
        createColumn('recipientEmail', 'Recipient Email'),
        createColumn('recipientName', 'Recipient Name'),
        createColumn('yearMonth', 'Month', displayYearMonth),
        createColumn('templateName', 'Template'),
        createColumn('type', 'Type', (type, emailLog) => {
            switch (type) {
                case 'AutoReminder':
                    return 'Automatic reminder';
                case 'ManualReminder':
                    return (_jsxs(_Fragment, { children: ["Manually sent ", _jsxs("i", { children: ["(by ", getFullName(emailLog.creator), ")"] })] }));
            }
        }),
    ];
    const options = {
        enableNestedDataAccess: '.',
        rowsPerPageOptions: [25, 50, 100],
        rowsPerPage: getDatatableConfig('emailsLogConfig', 'rowsPerPage') || 25,
        print: false,
        download: false,
        viewColumns: false,
        filter: false,
        storageKey: 'emailsLogConfig',
        selectableRowsHideCheckboxes: true,
        fixedHeader: true,
        setTableProps: () => {
            return {
                // material ui v5 only
                size: 'small',
            };
        },
    };
    const handleSave = (e) => __awaiter(this, void 0, void 0, function* () {
        setLoading(true);
        e.preventDefault();
        if (emailTemplate) {
            try {
                if (emailTemplate.id) {
                    setEmailTemplate(yield updateEmailTemplate(emailTemplate));
                    messageContext.setMessage(success('Template updated'));
                }
                else {
                    setEmailTemplate(yield createEmailTemplate(emailTemplate));
                    messageContext.setMessage(success('Template created'));
                }
            }
            catch (e) {
                messageContext.setMessage(error('Failed to save template'));
            }
        }
        setEditMode(false);
        setLoading(false);
    });
    const handleEdit = (e) => {
        e.preventDefault();
        setEditMode(true);
    };
    const handleReset = (e) => __awaiter(this, void 0, void 0, function* () {
        setLoading(true);
        e.preventDefault();
        if (emailTemplate) {
            try {
                if (emailTemplate.id) {
                    yield deleteEmailTemplate(emailTemplate.id);
                    setEmailTemplate(undefined);
                    messageContext.setMessage(success('Reset to use default Bas Ready template'));
                }
            }
            catch (e) {
                messageContext.setMessage(error('Failed to reset template'));
            }
        }
        setEditMode(false);
        setLoading(false);
    });
    const defaultSubjectBody = '{{basYearMonth}} Activity statement ready to be commenced';
    const defaultEmailBody = '<div>Hi {{contactFirstName}},</div>\n' +
        '      <br/>\n' +
        '      <div>Your {{basYearMonth}} Activity Statement for <em>{{organisationName}}</em> will be due shortly.\n' +
        '      There are {{numberUnreconciledBankTransactions}} transactions that still need to be reconciled up to the end of {{basMonth}}.\n' +
        '      Please ensure the transactions are reconciled promptly to allow us enough time to complete the Activity Statement before the due date.</div>\n' +
        '      <br/>\n' +
        '      <div>If you have any queries, please do not hesitate to contact our office.</div>\n' +
        '      <br/>\n' +
        '      <div>Kind Regards,</div>\n' +
        '      <div>{{clientManagerFirstName}} {{clientManagerLastName}}</div>';
    return (user && (_jsxs("div", { style: { padding: '10px' }, children: [_jsx(SectionTitle, { children: "Emails" }), _jsxs(TabContext, { value: tab, children: [_jsx(Box, { sx: { borderBottom: 1, borderColor: 'divider' }, children: _jsxs(TabList, { onChange: handleChange, children: [_jsx(Tab, { label: "Email history", value: "1" }), _jsx(Tab, { label: "Email template", value: "2" })] }) }), _jsx(TabPanel, { value: "1", children: loading ? _jsx(Loading, {}) : _jsx(MUIDataTable, { title: "", data: emailLogs, columns: columns, options: options }) }), _jsx(TabPanel, { value: "2", children: loading ? (_jsx(Loading, {})) : (_jsxs(Grid, { width: 800, children: [_jsxs(Alert, { severity: "info", sx: { mb: 2 }, children: ["This template will be used for the following ", _jsx("i", { children: "(If no template is created below, the default template will be used)" }), ":", _jsx("br", {}), _jsx("li", { children: "When sending automatic email reminders to the organisations of which you are the client manager" }), _jsx("li", { children: "As the editable template email when sending a manual reminder" })] }), _jsx("br", {}), _jsxs("form", { onSubmit: handleSave, children: [editMode ? (_jsx(TextField, { disabled: !editMode, required: true, label: "Template name", variant: "outlined", value: (emailTemplate === null || emailTemplate === void 0 ? void 0 : emailTemplate.name) || '', onChange: (e) => updateEmailTemplateState({ name: e.target.value }), fullWidth: true, sx: { mb: 3 } })) : (_jsxs("div", { style: {
                                                marginBottom: '10px',
                                                border: '1px solid #007ca3',
                                                color: '#007ca3',
                                                borderRadius: '5px',
                                                padding: '15px 0px',
                                                textAlign: 'center',
                                            }, children: ["Template: ", (emailTemplate === null || emailTemplate === void 0 ? void 0 : emailTemplate.name) || _jsx("i", { children: "Using default template" })] })), _jsx("div", { style: { marginLeft: '4px', marginBottom: '4px' }, children: "Subject" }), editMode ? (_jsx(Editor, { onContentUpdate: (subject) => updateEmailTemplateState({ subject }), initialState: (emailTemplate === null || emailTemplate === void 0 ? void 0 : emailTemplate.subject) || '', isHtml: false, showToolbar: true, placeholder: 'Start typing email subject...', tags: EMAIL_TAGS })) : (_jsx("div", { style: {
                                                border: '1px dotted #ccc',
                                                padding: '12px 15px',
                                                color: 'rgba(0, 0, 0, 0.38)',
                                            }, children: (emailTemplate === null || emailTemplate === void 0 ? void 0 : emailTemplate.subject) || defaultSubjectBody })), _jsx("br", {}), _jsx("div", { style: { marginLeft: '4px', marginBottom: '4px' }, children: "Body" }), editMode ? (_jsx(Editor, { onContentUpdate: (body) => updateEmailTemplateState({ body }), initialState: (emailTemplate === null || emailTemplate === void 0 ? void 0 : emailTemplate.body) || '', isHtml: true, showToolbar: true, placeholder: 'Start typing message body...', tags: EMAIL_TAGS })) : (_jsx("div", { style: { border: '1px solid #ccc', padding: '12px 15px', color: 'rgba(0, 0, 0, 0.38)' }, dangerouslySetInnerHTML: { __html: (emailTemplate === null || emailTemplate === void 0 ? void 0 : emailTemplate.body) || defaultEmailBody } })), _jsx("br", {}), _jsxs("div", { style: { display: 'flex', justifyContent: 'space-between' }, children: [_jsx(Button, { variant: "outlined", color: "warning", type: "reset", onClick: handleReset, disabled: (emailTemplate === null || emailTemplate === void 0 ? void 0 : emailTemplate.id) == undefined, children: "Reset to default" }), editMode ? (_jsx(Button, { variant: "contained", type: "submit", disabled: isBlankHtml(emailTemplate === null || emailTemplate === void 0 ? void 0 : emailTemplate.body) || isBlank(emailTemplate === null || emailTemplate === void 0 ? void 0 : emailTemplate.subject), children: "Save template" })) : (_jsx(Button, { variant: "contained", type: "submit", onClick: handleEdit, children: emailTemplate ? 'Edit template' : 'Create custom template' }))] })] })] })) })] })] })));
}
